import { ClerkProvider } from '@clerk/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { type Container, createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { PostHogProvider } from '@/components/PostHogProvider'
import { TooltipProvider } from '@/components/ui/Tooltip'
import { AuthProvider } from '@/hooks/useAuth'
import { getEnvironmentConfig } from '@/lib/environment.ts'
import { CookieManagerProvider } from '@/providers/CookieManagerProvider'
import { FeatureFlagsProvider } from '@/providers/FeatureFlagsProvider'
import { ModeProvider } from '@/providers/ModeProvider'
import { StellaProvider } from '@/providers/StellaProvider'
import { ToasterProvider } from '@/providers/ToasterProvider'
import { router } from '@/router.tsx'

import '@/index.css'

const config = getEnvironmentConfig()

const PUBLISHABLE_KEY = config.clerkPublishableKey

if (!PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key')
}

const root: Container = document.getElementById('root') as HTMLElement

const queryClient = new QueryClient()

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary>
      <PostHogProvider>
        <QueryClientProvider client={queryClient}>
          <FeatureFlagsProvider>
            <StellaProvider>
              <ModeProvider>
                <ClerkProvider
                  publishableKey={PUBLISHABLE_KEY}
                  touchSession={false}
                >
                  <AuthProvider>
                    <CookieManagerProvider>
                      <TooltipProvider>
                        <RouterProvider router={router} />
                        <ToasterProvider />
                      </TooltipProvider>
                    </CookieManagerProvider>
                  </AuthProvider>
                </ClerkProvider>
              </ModeProvider>
            </StellaProvider>
          </FeatureFlagsProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </ErrorBoundary>
  </StrictMode>
)
